import { useCallback, useEffect, useState } from 'react';
import { NavLink, Route, Routes } from 'react-router-dom';
import { api } from './lib/api';
import type { Principal } from './types';
import Login from './pages/Login';
import DashboardPage from './pages/Dashboard';
import ParentsPage from './pages/Parents';
import ParentDetailPage from './pages/ParentDetail';
import LambdasPage from './pages/Lambdas';
import LambdaDetailPage from './pages/LambdaDetail';
import SnowflakePage from './pages/Snowflake';
import PublishPage from './pages/Publish';
import ResolvePage from './pages/Resolve';
import MatrixPage from './pages/Matrix';
import OperationsPage from './pages/Operations';
import UsersPage from './pages/Users';

export default function App() {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api
      .me()
      .then(setPrincipal)
      .catch(() => setPrincipal(null))
      .finally(() => setChecked(true));
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setPrincipal(null);
  }, []);

  if (!checked) return <div className="login">Loading…</div>;
  if (!principal) return <Login onSignedIn={setPrincipal} />;

  return (
    <div className="shell">
      <nav className="sidebar">
        <div className="brand">
          Centralized DSP
          <small>dsps.skybridgeloans.com</small>
        </div>

        <NavLink to="/" end className={navClass}>
          Dashboard
        </NavLink>
        <NavLink to="/operations" className={navClass}>
          How config works
        </NavLink>
        <NavLink to="/matrix" className={navClass}>
          Config matrix
        </NavLink>

        <div className="nav-group">Function domains</div>
        <NavLink to="/settlement" className={navClass}>
          Settlement Operations
        </NavLink>
        <NavLink to="/lead-scoring" className={navClass}>
          Lead Scoring
        </NavLink>
        <NavLink to="/general" className={navClass}>
          General Lambdas
        </NavLink>

        <div className="nav-group">Configuration</div>
        <NavLink to="/parents" className={navClass}>
          DSP parents
        </NavLink>
        <NavLink to="/snowflake" className={navClass}>
          Snowflake profiles
        </NavLink>

        <div className="nav-group">Operations</div>
        <NavLink to="/resolve" className={navClass}>
          Resolve partner
        </NavLink>
        <NavLink to="/publish" className={navClass}>
          Validate &amp; publish
        </NavLink>
        {principal.role === 'admin' && (
          <NavLink to="/users" className={navClass}>
            Users &amp; audit
          </NavLink>
        )}

        <div className="sidebar-footer">
          <div>{principal.display_name}</div>
          <div className="small">{principal.role}</div>
          <button style={{ marginTop: 8, width: '100%' }} onClick={signOut}>
            Sign out
          </button>
        </div>
      </nav>

      <main className="main">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/operations" element={<OperationsPage />} />
          <Route path="/matrix" element={<MatrixPage />} />
          <Route
            path="/settlement"
            element={<LambdasPage opsArea="settlement_operations" />}
          />
          <Route path="/lead-scoring" element={<LambdasPage opsArea="lead_scoring" />} />
          <Route path="/general" element={<LambdasPage opsArea="general" />} />
          <Route path="/lambdas" element={<LambdasPage opsArea="settlement_operations" />} />
          <Route path="/parents" element={<ParentsPage principal={principal} />} />
          <Route path="/parents/:parentKey" element={<ParentDetailPage principal={principal} />} />
          <Route path="/lambdas/:lambdaKey" element={<LambdaDetailPage principal={principal} />} />
          <Route path="/snowflake" element={<SnowflakePage />} />
          <Route path="/resolve" element={<ResolvePage />} />
          <Route path="/publish" element={<PublishPage principal={principal} />} />
          <Route path="/users" element={<UsersPage principal={principal} />} />
          <Route path="*" element={<div className="callout warn">No such page.</div>} />
        </Routes>
      </main>
    </div>
  );
}

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? 'nav-link active' : 'nav-link';
}
